import { useCallback, useMemo } from 'react';

import { useUserStore } from '@/hooks/use-user-store';

import { PromoStatus, Promotion } from '../types';
import { getPromotionsToDisplay } from '../utils/promotions';
import { PROMOS_CACHE_TIME } from '../utils/promotions-utils';
import { usePromosStatus } from './use-promo-details';
import { usePromohub } from './use-promohub';
import { usePromotionsStore } from './use-promotions-store';
import { useSegmentedPromos } from './use-segmented-promos';

/**
 * Hook used to get the promotions displayed to the user.
 */
export const usePromotions = () => {
    const guest = useUserStore(state => state.guest);
    const { data: promohub, refetch: refetchPromohub, isLoading: loadingPromohub } = usePromohub();
    const {
        data: bonusEnginePromos,
        refetch: refetchBonusEnginePromos,
        isLoading: loadingBonusEnginePromos,
    } = useSegmentedPromos({ enabled: true, status: PromoStatus.AVAILABLE });
    const lastFetch = usePromotionsStore(state => state.lastFetch);
    const setLastFetch = usePromotionsStore(state => state.actions.setLastFetch);

    const nonSegmentedPromos = useMemo(
        () => promohub?.promotions?.filter(p => !p.data.segmented) ?? [],
        [promohub?.promotions]
    );

    const {
        data: genericPromosStatus,
        refetch: refetchGenericPromosStatus,
        isLoading: loadingGenericPromosStatuses,
    } = usePromosStatus(
        nonSegmentedPromos.map(p => p.uid),
        true
    );

    const isLoading = loadingPromohub || (loadingBonusEnginePromos && !guest) || loadingGenericPromosStatuses;

    const refetch = useCallback(
        (opts: { force: boolean } = { force: false }) => {
            const now = Date.now();
            const delta = now - lastFetch;
            if (delta > PROMOS_CACHE_TIME || opts.force) {
                setLastFetch(now);
                refetchPromohub();
                if (!guest) {
                    // don't fetch segmented stuff for guests
                    refetchBonusEnginePromos();
                    if (nonSegmentedPromos.length > 0) {
                        // fetch generic promos status only if there are generic promos
                        refetchGenericPromosStatus();
                    }
                }
            }
        },
        [
            guest,
            nonSegmentedPromos.length,
            refetchBonusEnginePromos,
            refetchGenericPromosStatus,
            refetchPromohub,
            lastFetch,
            setLastFetch,
        ]
    );

    const promotionsToDisplay = useMemo(() => {
        return getPromotionsToDisplay(bonusEnginePromos, promohub?.promotions, genericPromosStatus ?? []);
    }, [bonusEnginePromos, genericPromosStatus, promohub?.promotions]);

    // display not opted in promos first, keeping the original order within each group.
    // using locallyOptedInPromos to avoid reordering promos in real time when user opts in to a promo.
    const locallyOptedInPromos = usePromotionsStore(state => state.locallyOptedInPromos);

    const sortedPromotions = useMemo(() => {
        const sortPriority = (promo: Promotion) => (promo.optedIn && !locallyOptedInPromos.includes(promo.uid) ? 1 : 0);

        return [...promotionsToDisplay].sort((a, b) => sortPriority(a) - sortPriority(b));
    }, [locallyOptedInPromos, promotionsToDisplay]);

    return {
        promotions: sortedPromotions,
        bonusEnginePromos,
        refetch,
        isLoading,
        title: promohub?.title,
        showRgFooter: promohub?.showRgFooter,
    };
};
